import { Component, Prop, State, h, Element } from '@stencil/core';
import Keycloak from "keycloak-js";
import { toSvg } from "jdenticon";
import { AppConfig, configService } from '../../services/config';
import { MenuLink, menuService } from '../../services/menu';

@Component({
  tag: 'dash-button',
  styleUrl: 'dash-button.css',
  shadow: true,
})
export class DashButtonComponent {

  @Element() el;
  
  @Prop() showPostLoginText: boolean = false;
  @Prop() silentCheckSso: boolean = false;
  @Prop() redirectUri: string;
  @Prop() authMethod: string = "check-sso";

  @Prop() portalApiUrl: string = "";
  @Prop() plmUrl: string = "https://productlicensemanager-zdzw.cloud.zdzw-project.com";
  @Prop() showUnauthorizedModal: boolean = false;

  @Prop() primaryColor: string = "";
  @Prop() accentColor: string = "";

  @State() keycloak: any = null;

  @State() isAuth = false;
  @State() givenUserName: string = "";
  @State() silentCheckSsoRedirectUri: string =  window.location.origin + '/assets/verificar-sso.html';

  @State() appList = []
  
  @State() avatarSvg = toSvg("init-avatar", 40);

  @State() isLoading: boolean = false;
  @State() config: AppConfig;

  @State() localMenuLinks: MenuLink[];
  @State() organizationUrl: any;

  @State() isRestricted: boolean = false;

  async componentWillLoad() {

    // Fetch config from API
    this.config = await configService.fetchConfig(this.portalApiUrl);
    console.log(this.config)

    if (!this.config) return; // prevent Keycloak init if fetch failed

    // render will show spinner
    if(this.config?.loading?.isLoading) {
      this.isLoading = true;
      setTimeout(() => {
        this.isLoading = false;
      }, this.config?.loading?.duration);
    }

    // Initialize Keycloak
    this.keycloak = new Keycloak({
      url: this.config.keycloak.url,
      realm: this.config.keycloak.realm,
      clientId: this.config.keycloak.clientId
    });

    //
    // Init Keycloak
    //
    this.initKeycloak().then(() => {
      // Check if the user is authenticated
      if(this.isAuth) {
        // Get organization URL and load local menu items
        // If the user has not set an organization URL, they can configure it in the Portal profile page
        this.loadOrganizationUrlAndLinks();
  
        // Validate user access permission for the application. 
        // If unauthorized, display the restricted access modal.
        this.validateAppAccessByUrl()

      }
    })
  }

  async initKeycloak() {
    // Remove the initiation time permission localstorage object
    localStorage.removeItem("ZDZW_app_permission");

    await this.keycloak.init(
      {
          onLoad: this.authMethod,
          checkLoginIframe: false,
          silentCheckSsoRedirectUri: this.silentCheckSso ? this.silentCheckSsoRedirectUri : '',
      }).then(res => {
        if(res) {
          this.isAuth = true
          localStorage.setItem("keycloak", JSON.stringify(this.keycloak));
          // document.cookie = "DASH_BTN=" + JSON.parse(localStorage.getItem('keycloak')).token;
          document.cookie = this.config.cookieName +"=" + JSON.parse(localStorage.getItem('keycloak')).token;

          // Set userName
          this.givenUserName = JSON.parse(localStorage.getItem('keycloak')).idTokenParsed.preferred_username

          // Set user avatar
          this.avatarSvg = toSvg(JSON.parse(localStorage.getItem('keycloak')).idTokenParsed.sub, 40);
        } 
      });
  }

  async loadOrganizationUrlAndLinks() {
    // Read org URL safely
    try {
      const kc = JSON.parse(localStorage.getItem("keycloak") || "{}");
      this.organizationUrl = kc?.idTokenParsed?.organizationUrl || null;
    } catch {
      this.organizationUrl = null;
    }

    // If no organization, do nothing
    if (!this.organizationUrl) return;

    // If config or menu not ready, skip
    if (!this.config?.menu?.local) return;

    // Only load when custom menu is NOT enabled
    if (!this.config.menu.local.enableCustomLocalMenu) {
      try {
        this.localMenuLinks = await menuService.getMenuLinks(
          this.organizationUrl
        );
      } catch (e) {
        console.error("Failed to load local menu", e);
        this.localMenuLinks = [];
      }
    }
  }

  // First, check global links
  // Then, check the token's organization URL
  // Finally, check against dashbutton configuration: allowedGlobalDomain and whitelistedUrls
  validateAppAccessByUrl() {

    const normalizeUrl = (url: string) => url.replace(/\/+$/, '');

    const currentUrl = normalizeUrl(window.location.href);
    const currentOrigin = normalizeUrl(window.location.origin);

    const globalLinks = this.config?.menu?.global?.links || [];
    const allowedGlobalDomain = this.config?.allowedGlobalDomain || '';
    const whitelistedUrls = this.config?.whitelistedUrls || '';

    // First, check global links
    const matchGlobal = globalLinks.some(link => {
      try {
        const linkOrigin = normalizeUrl(new URL(link.url).origin);
        return currentOrigin === linkOrigin;
      } catch {
        return false;
      }
    });

    // Then check the token organization URL
    const tokenParsed = this.keycloak?.tokenParsed;
    const allowedOrg = tokenParsed?.organizationUrl;
    const matchOrganization =
      allowedOrg && currentUrl.includes(normalizeUrl(allowedOrg));

    // Check allowedGlobalDomain (single domain)
    const matchAllowedDomain =
      allowedGlobalDomain &&
      currentOrigin.includes(normalizeUrl(allowedGlobalDomain));

    // Check whitelistedUrls (comma-separated string)
    const whitelistedArray = whitelistedUrls
      ? whitelistedUrls.split(',').map(url => normalizeUrl(url.trim()))
      : [];

    const matchWhitelisted = whitelistedArray.some(url =>
      currentUrl.includes(url)
    );

    // Final validation
    const isAllowed =
      matchGlobal ||
      matchOrganization ||
      matchAllowedDomain ||
      matchWhitelisted;

    this.isRestricted = !isAllowed;
  }

  login() {  
    this.keycloak.login()        
  }

  logout() {
    localStorage.removeItem("keycloak");
    // document.cookie = "DASH_BTN=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    document.cookie = this.config.cookieName + "=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    this.keycloak.logout() 
  }  

  showModal() {
    const appModal = this.el.shadowRoot.querySelector('#appListModal');
    appModal.style.display = "block";
  }

  closeModal() {
    const appModal = this.el.shadowRoot.querySelector('#appListModal');
    appModal.style.display = "none";
  }

  render() {
    return <div>
    <div>
        {
          (() => {
              // GLOBAL LOADING SPINNER
              if (this.isLoading) {
                return (
                  <a id="login-btn" class="button button-link">
                    <div class="lds-ring"><div></div><div></div><div></div><div></div></div>
                  </a>
                );
              }

              if (this.isRestricted) {
                return (
                  <div class="restricted-overlay">
                    <div class="restricted-card">

                      <div class="restricted-icon">
                        <i class="fa-solid fa-shield-halved"></i>
                      </div>

                      <h2>Access Restricted</h2>
                      <p class="restricted-message">
                        You do not have permission to access this application.
                        Please contact your administrator if you believe this is an error.
                      </p>

                      {/* Global Applications */}
                      {this.config?.menu?.global?.links?.length > 0 && (
                        <div class="restricted-links">
                          <h4>Publicly Available Modules</h4>
                          <div class="link-grid">
                            {this.config.menu.global.links.map(item => (
                              <a href={item.url} target="_blank" class="restricted-link">
                                <i class={item.icon}></i>
                                <span>{item.name}</span>
                              </a>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Actions */}
                      <div class="restricted-actions">
                        {this.config?.defaultLink?.url && (
                          <a
                            href={this.config.defaultLink.url}
                            target="_blank"
                            class="btn-primary">
                            Go to Portal
                          </a>
                        )}
                        <button class="btn-danger" onClick={this.logout.bind(this)}>
                          Logout
                        </button>
                      </div>

                    </div>
                  </div>
                );
              }

              if(this.isAuth  && this.showPostLoginText) { 
                  return (
                    <a style={{background: this.primaryColor != undefined? this.primaryColor : ""}} href={this.redirectUri} id="login-btn" class="button button-link">
                      <span style={{color: this.accentColor != undefined? this.accentColor : ""}} class="button-text button-text-full-width">Go to Dashboard</span>
                    </a>
                  )
                } else if (this.isAuth) {
                    return (
                      <div id="profile-btn" class="dropdown">
                      <button style={{background: this.primaryColor != undefined? this.primaryColor : ""}} type="button" class="button">
                          <span class="button-profile">
                            {JSON.parse(localStorage.getItem('keycloak')).idTokenParsed.imageUrl ?
                              <img class="profile-image" src="https://s-damith.github.io/test-avatar/images/e24da9c5-0b1f-46fa-82cd-7c711c77b5c7.png"/>
                            :
                              <div innerHTML={this.avatarSvg}/>
                            }
                          </span>
                          <span style={{color: this.accentColor != undefined? this.accentColor : ""}} id="given-name" class="button-text">{this.givenUserName}</span>
                          <div class="button-icon">
                              {(this.config.menuViewType == "grid") ? <i class="fa-solid fa-grip-vertical"></i> : <i class="fa-solid fa-bars"></i>} 
                          </div>
                      </button>
                      <div class="dropdown-content">
                          <div class="dropdown-user-profile">
                            <div class="username">@{this.givenUserName}</div>
                            <div class="first-last-name">{JSON.parse(localStorage.getItem('keycloak')).idTokenParsed.name}</div>
                          </div>


                            { (this.config.menuViewType == "grid") ? (
                               <div>
                                <div class="grid-view-menu">
                                  {
                                    this.config.menu.global.links.map(item => {
                                      return (
                                        <a href={item.url} target='_blank' class="grid-view-link">
                                          <i class={item.icon + " fa-2x"}></i>    
                                          <div class="grid-view-link-text">{item.name}</div>
                                        </a>
                                      )
                                    })
                                  }
                                </div>
                                
                                {/* show divider */}
                                {this.config.menu.global.links.length != 0 && <hr class="solid"></hr>}

                                  <div class="grid-view-menu" style={this.config.menu.global.links.length == 0 && {"margin-bottom": "12px"}}>
                                    <a  onClick={this.showModal.bind(this)} class="grid-view-link">
                                      <i class="fa-regular fa-window-restore fa-2x"></i>    
                                      <div class="grid-view-link-text">Modules</div>
                                    </a>
                                    <a href={this.config.defaultLink.url} target='_blank' class="grid-view-link">
                                      <i class="fa-solid fa-globe fa-2x"></i>
                                      <div class="grid-view-link-text">{this.config.defaultLink.name}</div>
                                    </a>
                                    <a onClick={this.logout.bind(this)} class="grid-view-link">
                                      <i class="fa-solid fa-right-from-bracket fa-2x"></i>
                                      <div class="grid-view-link-text">Logout</div>
                                    </a>
                                    
                                  </div>

                                </div>
                                ) : (

                                // List View
                                <div class="dropdown-content-link">
                                  {
                                    this.config.menu.global.links.map(item => {
                                      return (
                                        <a href={item.url} target='_blank' id="applications-btn">
                                          <i class={item.icon + " menu-bar-icons"}></i>    
                                          {item.name}
                                        </a>
                                      )
                                    })
                                  }
                                  <a onClick={this.showModal.bind(this)} id="applications-btn"><i class="fa-regular fa-window-restore menu-bar-icons"></i>Modules</a>
                                  <a href={this.config.defaultLink.url} target='_blank' id="applications-btn"><i class="fa-solid fa-globe menu-bar-icons"></i>{this.config.defaultLink.name}</a>
                                  <a onClick={this.logout.bind(this)} id="logout-btn"><i class="fa-solid fa-right-from-bracket menu-bar-icons"></i>Logout</a>
                                </div>

                            )}
                      </div>
                    </div>
                    )
                } else {
                  return (
                    <button style={{background: this.primaryColor != undefined? this.primaryColor : ""}} onClick={this.login.bind(this)} id="login-btn" type="button" class="button">
                      <span style={{color: this.accentColor != undefined? this.accentColor : ""}} class="button-text">Login</span>
                    </button>
                  )
                }
          })()  
        } 
    </div>
  
    <div id="appListModal" class="modal">
    
      <div class="modal-content">
        <span onClick={this.closeModal.bind(this)} class="close">&times;</span>

        <div class="box">
          {<input type="radio" class="tab-toggle" name="tab-toggle" id="tab1" checked/>}
          {/* {(this.menuLinkList.length != 0) && <input type="radio" class="tab-toggle" name="tab-toggle" id="tab2" checked={this.disableApplicationTab}/>} */}

          <ul class="tab-list">
            {<li class="tab-item">
              <label class="tab-trigger" htmlFor="tab1"><i class="fa-solid fa-table-list menu-bar-icons"></i> {this.config.menu.local.name}</label>
            </li>}
            {/* {(this.menuLinkList.length != 0) && <li class="tab-item">
              <label class="tab-trigger" htmlFor="tab2"><i class="fa-solid fa-link menu-bar-icons"></i> {this.otherLinkDisplayName}</label>
            </li>} */}
          </ul>
          
          <div class="tab-container">
          <div class="tab-content">
              <div class="modal-body">

                {!this.config?.menu?.local?.enableCustomLocalMenu ? (
                  // <div>{this.localMenuLinks}</div>
                  this.localMenuLinks?.map((app, index) => {
                    const isDisabled = !app.url;

                    return ( 
                      <a
                        key={index}
                        class="external-app-link"
                        href={isDisabled ? undefined : app.url}
                        target={isDisabled ? undefined : "_blank"}
                        rel={isDisabled ? undefined : "noopener noreferrer"}
                        onClick={isDisabled ? (e) => e.preventDefault() : undefined}
                        >
                          <div class="modal-app">
                            <div>{app.name}</div>
                          </div>
                      </a>
                    )
                })
                ) : (
                  this.config.menu.local.links?.map((app, index) => (
                    <a
                      key={index}
                      class="external-app-link"
                      target="_blank"
                      rel="noopener noreferrer"
                      href={app.url}
                    >
                      <div class="modal-app">
                        <div>{app.name}</div>
                      </div>
                    </a>
                  ))
                )}

                {/* Error messages */}
                {!this.organizationUrl ? (
                  <div>
                    <h3 class="gray-text">
                      Organization Domain URL not set
                    </h3>
                    <p class="gray-text">Please set your Organization Domain URL on the Portal profile page.</p>
                  </div>
                ) : (
                  (
                    (!this.config?.menu?.local?.enableCustomLocalMenu &&
                      (this.localMenuLinks?.length ?? 0) === 0) ||
                    (this.config?.menu?.local?.enableCustomLocalMenu &&
                      (this.config?.menu?.local?.links?.length ?? 0) === 0)
                  ) && (
                    <div>
                      <h3 class="gray-text">
                        No installed {this.config?.menu?.local?.name || "menu"} found.
                      </h3>
                      <p class="gray-text">Please contact administration.</p>
                    </div>
                  )
                )}

              </div>
            </div> 
            {/* <div class="tab-content">
              <div class="modal-body">
                {
                  this.menuLinkList.map(link => {
                    return (
                      <a class="external-app-link" target='_blank' href={link.url}>
                        <div class="modal-app">
                          <div>{link.name}</div>
                        </div>
                      </a>
                    )
                  })
                }
              </div>
            </div> */}
          </div>
        </div>

      </div>
    
    </div>

    <div id="appPermissionErrorModal" class="modal">
      <div class="modal-content">
        <img src="data:image/png;base64,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"/>
        <h2 class="green-text margin-b">You don't have permission to access this application.</h2>
        <h3 class="green-text">Please contact the administration.</h3>
          
          {(this.config.defaultLink.url && this.showUnauthorizedModal) ? 
              <div>
                <h4 class="gray-text">You will be automatically redirected to the portal.</h4>
                  <div id="countdown">
                  <div id="countdown-number"></div>
                  <svg>
                    <circle r="18" cx="20" cy="20"></circle>
                  </svg>
                </div>
              </div>
            : <div></div>
          }
      </div>
    </div>

  </div>;
  }
}
